import SelectionSynchronizer, {
  type SelectionState,
  type UserInfo,
} from './SelectionSynchronizer';
import Debug from 'debug';
import CollaborativeDocument from './CollaborativeDocument';
import { type Connection } from 'sharedb/lib/client';
import { type as otSlateType } from '../ot-slate-v2';
import { type Client } from 'faye';

const debug = Debug('slate-collaboration:presence');

// Document selections for each of the collaborators, indexed by the selectionId.
export interface SelectionMap {
  [key: string]: SelectionState;
}

export type FetchUserInfo = ({
  userId,
}: {
  userId: string;
}) => Promise<{ displayName: string }>;

export default class DocumentWithPresence extends CollaborativeDocument {
  // Synchronize selections between all collaborators on the document over
  // a Faye websocket connection.
  selectionSynchronizer: SelectionSynchronizer;

  // Periodic timers.
  intervals: {
    clearExpired?: NodeJS.Timeout;
    resendSelection?: NodeJS.Timeout;
  };

  selections: SelectionMap = {};

  constructor(options: {
    fayeClient: Client;
    connection: Connection;
    collection: string;
    documentId: string;
  }) {
    super(options.connection, options.collection, options.documentId);
    this.selections = {};
    this.intervals = {};
    this.selectionSynchronizer = new SelectionSynchronizer({
      fayeClient: options.fayeClient,
      onSelectionUpdate: this._transformAndUpdateSelection,
      documentPath: this.identifier,
    });
    this._setupCollaborationCallbacks();
  }

  setUserInfo(userInfo: UserInfo) {
    this.selectionSynchronizer.setUserInfo(userInfo);
  }

  connect(onSubscribed) {
    super.connect(() => {
      this.intervals.clearExpired = setInterval(
        this._clearExpiredSelections,
        10000
      );
      if (onSubscribed) onSubscribed();
    });

    return this;
  }

  close(callback: (err: any) => void) {
    if (this.intervals.clearExpired) clearInterval(this.intervals.clearExpired);
    if (this.intervals.resendSelection)
      clearInterval(this.intervals.resendSelection);

    this.selectionSynchronizer.stopSynchronizing();
    super.close(callback);
  }

  submitChange(change) {
    const ops = change.operations.filter(
      (op) => op.type !== 'set_selection' && op.type !== 'set_value'
    );

    if (ops.length > 0) {
      this._transformSelections(ops);
    }

    super.submitChange(change);
  }

  // Transforms incoming selections as they're received, and updates the UI.
  _transformAndUpdateSelection = (selection) => {
    // TODO: Reenable after I figure out selection
    // if (selection.selection.isFocused) {
    const transformedSelection = this._transformSelection(
      selection,
      this.pendingOperations
    );
    this.selections[transformedSelection.selectionId] = transformedSelection;
    // } else {
    //   delete this.selections[selection.selectionId];
    // }
  };

  _transformSelection(selectionData, ops) {
    const transformedSelectionData =
      this.selectionSynchronizer.transformSelection(
        selectionData,
        (selection) => otSlateType.transformSelection(selection, ops)
      );

    if (debug.enabled) {
      debug(
        `transformed selection ${JSON.stringify(
          selectionData
        )}\n\nagainst ${JSON.stringify(ops)}\n\ninto ${JSON.stringify(
          transformedSelectionData
        )}`
      );
    }
    return transformedSelectionData;
  }

  _setupCollaborationCallbacks() {
    this.onUpdate((editor, version) => {
      if (!this.hasPendingWrites()) {
        this._sendSelection(editor, version);
      }
    });

    this.onRollback(() => {
      this.selections = {};
    });

    this.onVersionChanged((newVersion) => {
      this.selectionSynchronizer.didUpdateDocumentToVersion(newVersion);
    });

    this.onOperationsAcknowledged((snapshot, version) => {
      // In order for a selection we send to be valid, it has to be:
      //
      //   - On a version of the document that has all remote changes
      //   - On a version of the document that has *only* our changes
      //     that have been acknowledged by the server
      //
      // Since there's no good way I can find to create a document
      // like that at this point, we'll wait until our doc is
      // completely up-to-date with the server before sending our
      // current selection. This delays our selections, but since
      // remote clients will be transforming them anyway as they
      // receive our changes, it's not all that noticeable.

      // TODO: Some other possible options:
      //
      //   Still use change.value when sending operations, but transform
      //   the value as ops come in, or queue the ops as the selection
      //   goes out. I don't think this will actually work, because the
      //   ops I'm yielded have already been transformed against pending ops.
      //
      //   I could invert all the queued / pending ops, apply the
      //   inverses to the snapshot, and send the resulting
      //   selection. This only works if the pending ops have been
      //   transformed against the incoming ops so that they're
      //   invertable on the new snapshot, which I think they should be.
      //
      //   A slightly hackier way would be to see if any remote ops came
      //   in (through doc version, maybe) and only send with pending
      //   writes if no reads have come in. Then I can still use change.value.
      if (!this.hasPendingWrites()) {
        this._sendSelection(snapshot, version);
      }
    });

    this.onRemoteOperationsReceived((operations) => {
      this._transformSelections(operations);
    });
  }

  _sendSelection(snapshot, version) {
    this.selectionSynchronizer.sendSelection(snapshot, version);
    // TODO: Propagate focus state to the snapshot
    // if (snapshot.selection.isFocused && !this.intervals.resendSelection) {
    if (!this.intervals.resendSelection) {
      // If the selection is focused, send heartbeat updates.
      this.intervals.resendSelection = setInterval(
        this._resendCurrentSelection,
        15000
      );
    }
    // else if (!snapshot.selection.isFocused) {
    //   // Otherwise, the selection won't get displayed anyway, so don't
    //   // send updates.
    //   clearInterval(this.intervals.resendSelection);
    //   this.intervals.resendSelection = null;
    // }
  }

  _transformSelections(operations) {
    for (const [id, selection] of Object.entries(this.selections)) {
      this.selections[id] = this._transformSelection(selection, [operations]);
    }
  }

  _clearExpiredSelections = () => {
    for (const [id, selection] of Object.entries(this.selections)) {
      if (selection.receivedAt && selection.receivedAt < Date.now() - 30000) {
        delete this.selections[id];
      }
    }
  };

  _resendCurrentSelection = () => {
    this.selectionSynchronizer.resendSelection();
  };
}
